import React, {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from 'react';
import {
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import type { ExpandedState } from '@tanstack/react-table';
import { useOverlayScrollbars } from 'overlayscrollbars-react';
import classNames from 'classnames';
import 'overlayscrollbars/styles/overlayscrollbars.css';
import { Button } from '@saleshandy/design-system';
import EditTableColumnsModal from '../../components/molecules/edit-table-columns-modal';
import Alert from '../alert';

import { TanStackTableProps, ColumnField } from './types';
import THead from './components/thead';
import TBody from './components/tbody';
import MemoizedTBody from './components/memoized-tbody';
import TablePagination from './components/table-pagination';
import ColumnVisibilityControl from './components/column-visibility-control';
import LeadSelectionBanner from './components/lead-selection-banner';

const NOOP = () => {};

function pluralizeWord(word: string, count: number): string {
  if (count === 1) return word;

  // Special-case the most visible irregular noun for our tables.
  if (word.toLowerCase() === 'company') {
    return word[0] === 'C' ? 'Companies' : 'companies';
  }

  const endsWithY = /[^aeiou]y$/i.test(word);
  if (endsWithY) {
    return `${word.slice(0, -1)}ies`;
  }
  const endsWithSibilant = /(s|x|z|ch|sh)$/i.test(word);
  if (endsWithSibilant) {
    return `${word}es`;
  }
  return `${word}s`;
}

const TanStackTable = <TData extends Record<string, any>>({
  columns,
  rows,
  onRowClick = null,
  sort = null,
  onSort = null,
  pinnedColumns = [],
  rightPinnedColumns = [],
  defaultPinnedColumns = [],
  immovableColumns = [],
  onFreezeColumn = null,
  onUnfreezeColumn = null,
  paginationOptions,
  isLoading = false,
  selectedRowIds = [],
  onRowSelect = null,
  deferRowSelect = false,
  onRowSelectAll = null,
  onClearSelection = null,
  maxSelectedRowsLimit = null,
  isSelectAllAcrossPages = false,
  onSelectAllAcrossPages = null,
  allowIndivualRowDeSelectionAcrossAllPage = false,
  selectAllDisplayCount: customSelectAllDisplayCount = null,
  enableSetLimit = false,
  setLimitMaxValue = null,
  appliedLimit = null,
  onApplyLimit = null,
  onApplyCurrentPage = null,
  maxCustomLimit = null,
  isCustomLimitMode = false,
  isRowDisabled = null,
  getRowDisabledTooltip = null,
  enableCappedSelectAllUI = false,
  cellRenderer,
  enableColumnResizing = true,
  enableSorting = true,
  enableRowSelection = true,
  enableColumnPinning = true,
  enableColumnOrdering = false,
  onColumnOrderChange = null,
  scrollbarOptions = {
    autoHide: 'never',
    autoHideDelay: 500,
  },
  paginationSizeOptions = [25, 50, 100],
  disableGoToPage = false,
  maxPaginationCount,
  maxAllowedPage,
  paginationRestrictionTooltip,
  freePlanSelectionLimit = null,
  columnVisibility,
  itemName = 'row',
  tableClassName = '',
  syncLayoutOnContainerResize = false,
  enableProperPluralization = false,
  showBanner = false,
  Banner,
  showLeadSelectionBanner = false,
  enableExpandedRows = false,
  getSubRows,
  getRowClassName = null,
}: TanStackTableProps<TData>) => {
  const tableRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [tableWidth, setTableWidth] = useState<string | number>(0);
  const [data, setData] = useState(rows);
  const [showColumnModal, setShowColumnModal] = useState(false);
  const [selectedFields, setSelectedFields] = useState<ColumnField[]>([]);
  const saveColumnSizesTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [tableClientWidthState, setTableClientWidthState] = useState<number>(0);
  const [expanded, setExpanded] = useState<ExpandedState>({});

  // Load saved column sizes synchronously on initial render
  // Using a ref to ensure we only load once and preserve across renders
  const initialSavedSizesRef = useRef<Record<string, number> | null>(null);
  if (initialSavedSizesRef.current === null) {
    initialSavedSizesRef.current =
      columnVisibility?.onLoadColumnSizes?.() || {};
  }

  // State for column sizing - initialized with saved sizes
  const [columnSizing, setColumnSizing] = useState<Record<string, number>>(
    () => {
      const sizing: Record<string, number> = {};
      const savedSizes = initialSavedSizesRef.current || {};

      columns.forEach((column) => {
        const columnId = column.id as string;
        if (columnId) {
          // Priority: saved sizes > column definition sizes
          if (savedSizes[columnId]) {
            sizing[columnId] = savedSizes[columnId];
          } else if (column.size) {
            sizing[columnId] = column.size as number;
          }
        }
      });
      return sizing;
    },
  );

  // Initialize selected fields from column visibility config
  useEffect(() => {
    if (columnVisibility?.enableColumnVisibility) {
      // Try to load from preferences first
      if (columnVisibility.onLoadColumnPreferences) {
        const loadedFields = columnVisibility.onLoadColumnPreferences();
        if (loadedFields && loadedFields.length > 0) {
          setSelectedFields(loadedFields);
          return;
        }
      }
      // Fall back to provided selectedFields
      if (
        columnVisibility.selectedFields &&
        columnVisibility.selectedFields.length > 0
      ) {
        setSelectedFields(columnVisibility.selectedFields);
      }
    }
  }, [columnVisibility]);

  // Sync selectedFields when columnVisibility.selectedFields changes
  useEffect(() => {
    if (
      columnVisibility?.enableColumnVisibility &&
      columnVisibility.selectedFields
    ) {
      setSelectedFields(columnVisibility.selectedFields);
    }
  }, [columnVisibility?.selectedFields]);

  // Update column sizing when columns change (e.g., new columns added)
  useEffect(() => {
    const savedSizes = initialSavedSizesRef.current || {};
    setColumnSizing((prevSizing) => {
      const newSizing = { ...prevSizing };
      let hasChanges = false;

      columns.forEach((column) => {
        const columnId = column.id as string;
        if (columnId && !newSizing[columnId]) {
          // Add sizing for new columns
          if (savedSizes[columnId]) {
            newSizing[columnId] = savedSizes[columnId];
            hasChanges = true;
          } else if (column.size) {
            newSizing[columnId] = column.size as number;
            hasChanges = true;
          }
        }
      });

      return hasChanges ? newSizing : prevSizing;
    });
  }, [columns]);

  const table = useReactTable({
    data,
    columns,
    state: {
      columnSizing,
      columnPinning: {
        left: pinnedColumns,
        right: rightPinnedColumns,
      },
      ...(enableExpandedRows && { expanded }),
    },
    onColumnSizingChange: setColumnSizing,
    ...(enableExpandedRows && {
      onExpandedChange: (updater) =>
        setExpanded((prev) =>
          typeof updater === 'function' ? updater(prev) : updater,
        ),
    }),
    getCoreRowModel: getCoreRowModel(),
    ...(enableExpandedRows && {
      getRowId: (row: TData, index: number, parent: any) =>
        (row as any).id ?? (parent ? `${parent.id}-${index}` : String(index)),
      getSubRows: getSubRows ?? ((row: TData) => (row as any).subRows),
      getExpandedRowModel: getExpandedRowModel(),
    }),
    meta: {
      updateData: (rowIndex: number, columnId: string, value: any) => {
        setData((old) =>
          old.map((row, index) => {
            if (index === rowIndex) {
              return {
                ...row,
                [columnId]: value,
              };
            }
            return row;
          }),
        );
      },
    },
    enableRowSelection,
    enableColumnPinning,
    enableSorting,
    enableColumnResizing,
    columnResizeMode: 'onChange',
    defaultColumn: {
      maxSize: 1000,
      minSize: 50,
    },
  });

  // Extract stable values from table state to prevent unnecessary recalculations
  const {
    columnSizing: columnSizingState,
    columnSizingInfo,
  } = table.getState();
  const { isResizingColumn } = columnSizingInfo;

  // Use reactive state for table client width to ensure updates on resize
  const tableTotalSize = table.getTotalSize();
  const tableClientWidth =
    tableClientWidthState || tableRef?.current?.clientWidth || 0;

  const layoutWidth = syncLayoutOnContainerResize
    ? containerWidth
    : tableClientWidth;

  useLayoutEffect(() => {
    if (!syncLayoutOnContainerResize) {
      return undefined;
    }

    const el = tableRef.current;
    if (!el) {
      return undefined;
    }

    const syncWidth = () => {
      setContainerWidth(el.clientWidth);
    };

    syncWidth();
    const observer = new ResizeObserver(syncWidth);
    observer.observe(el);

    return () => {
      observer.disconnect();
    };
  }, [syncLayoutOnContainerResize]);

  const columnSizeVars = useMemo(() => {
    const headers = table.getFlatHeaders();
    const colSizes: { [key: string]: number } = {};
    const totalSize = tableTotalSize;
    const availableWidth = layoutWidth;
    const extraSpace =
      totalSize < availableWidth && headers.length > 0
        ? (availableWidth - totalSize) / headers.length
        : 0;

    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < headers.length; i += 1) {
      const header = headers[i];
      if (header) {
        // Always use column definition size as the base, then apply any resizing
        // This ensures consistent initial width from the first render
        const columnDefSize = (header.column.columnDef.size as number) || 0;
        const currentSize = header.getSize();
        // Prefer currentSize if it's been set (from resizing), otherwise use columnDefSize
        const finalSize =
          currentSize > 0 && currentSize !== columnDefSize
            ? currentSize
            : columnDefSize || 50;

        colSizes[`--header-${header.id}-size`] = finalSize + extraSpace;
        colSizes[`--col-${header.column.id}-size`] = finalSize + extraSpace;
      }
    }
    return colSizes;
  }, [
    columnSizingState,
    columnSizingInfo,
    layoutWidth,
    tableClientWidthState,
    tableTotalSize,
    columns,
  ]);

  // Track previous resizing state to detect when resizing ends
  const wasResizingRef = useRef(false);

  // Save column sizes when resizing ends (debounced)
  useEffect(() => {
    const wasResizing = wasResizingRef.current;
    const isCurrentlyResizing = !!isResizingColumn;

    // Update the ref for next render
    wasResizingRef.current = isCurrentlyResizing;

    // If resizing just ended, save the sizes
    if (
      wasResizing &&
      !isCurrentlyResizing &&
      columnVisibility?.onSaveColumnSizes
    ) {
      // Clear any pending save
      if (saveColumnSizesTimeoutRef.current) {
        clearTimeout(saveColumnSizesTimeoutRef.current);
      }

      // Debounce the save to avoid too many writes
      saveColumnSizesTimeoutRef.current = setTimeout(() => {
        // Use columnSizing state which is already being tracked
        const currentSizes: Record<string, number> = {};
        const headers = table.getFlatHeaders();

        headers.forEach((header) => {
          const columnId = header.column.id;
          const size = header.getSize();
          if (columnId && size > 0) {
            currentSizes[columnId] = size;
          }
        });

        // Save sizes to storage
        columnVisibility.onSaveColumnSizes(currentSizes);

        // Update the ref for future loads
        initialSavedSizesRef.current = currentSizes;
      }, 300);
    }

    // Cleanup timeout on unmount
    return () => {
      if (saveColumnSizesTimeoutRef.current) {
        clearTimeout(saveColumnSizesTimeoutRef.current);
      }
    };
  }, [isResizingColumn, columnVisibility?.onSaveColumnSizes, table]);

  // Memoize selectedRowIds as Set for O(1) lookup performance
  const selectedRowIdsSet = useMemo(() => new Set(selectedRowIds), [
    selectedRowIds,
  ]);

  // Memoize row IDs to prevent recalculation on every render
  const rowIds = useMemo(() => rows.map((r) => r.id), [rows]);

  
  const selectionLimitRef = useRef({
    count: selectedRowIds.length,
    max: maxSelectedRowsLimit,
  });
  selectionLimitRef.current = {
    count: selectedRowIds.length,
    max: maxSelectedRowsLimit,
  };

  const onRowSelectHandler = useCallback(
    (rowId: number, isSelect: boolean) => {
      if (!onRowSelect) return null;

      if (isSelect) {
        const { count, max } = selectionLimitRef.current;
        if (max && count >= max) {
          return false; // Limit reached
        }
      }
      // reason for deferRowSelect is to ensure the checkbox's optimistic state paints before the selection re-render runs
      if (deferRowSelect) {
        window.setTimeout(() => onRowSelect(rowId, isSelect), 0);
      } else {
        onRowSelect(rowId, isSelect);
      }
      return null;
    },
    [onRowSelect, deferRowSelect],
  );

  const onRowSelectAllHandler = useCallback(
    (isSelect: boolean) => {
      // Always pass rowIds to parent handler, let parent handle the logic
      // Parent will handle merging with other pages, limit checking, etc.
      if (onRowSelectAll) {
        onRowSelectAll(rowIds, isSelect);
      }
    },
    [onRowSelectAll, rowIds],
  );

  const [initialize, overlayScrollbarsInstance] = useOverlayScrollbars({
    options: {
      scrollbars: scrollbarOptions,
    },
    defer: true,
  });

  useEffect(() => {
    if (tableRef?.current) {
      initialize(tableRef.current);
    }
  }, [initialize, tableRef?.current]);

  useEffect(() => {
    let width: number;
    if (syncLayoutOnContainerResize) {
      if (!containerWidth) {
        return;
      }
      width = containerWidth;
    } else if (tableRef.current) {
      width = tableRef.current.clientWidth;
    } else {
      return;
    }
    setTableWidth(tableTotalSize < width ? '100%' : `${tableTotalSize}px`);
  }, [
    syncLayoutOnContainerResize,
    tableTotalSize,
    containerWidth,
    tableClientWidth,
  ]);
  // Initialize tableClientWidthState when ref is available
  useEffect(() => {
    if (tableRef?.current && tableClientWidthState === 0) {
      const { clientWidth } = tableRef.current;
      if (clientWidth > 0) {
        setTableClientWidthState(clientWidth);
      }
    }
  }, [tableClientWidthState]);

  // Use stable references for tableWidth calculation - now uses reactive tableClientWidthState
  useEffect(() => {
    if (tableRef?.current && tableClientWidthState > 0) {
      const newWidth =
        tableTotalSize < tableClientWidthState ? '100%' : `${tableTotalSize}px`;
      setTableWidth(newWidth);
    }
  }, [tableTotalSize, tableClientWidthState]);

  // Add window resize listener to update table width reactively
  useEffect(() => {
    const handleResize = () => {
      if (tableRef?.current) {
        const { clientWidth } = tableRef.current;
        // Update state which will trigger columnSizeVars and tableWidth recalculation
        setTableClientWidthState(clientWidth);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [tableTotalSize, tableWidth]);

  // Scroll to top when page changes
  useEffect(() => {
    if (paginationOptions?.page !== undefined) {
      const instance = overlayScrollbarsInstance();
      if (instance) {
        const { viewport } = instance.elements();
        viewport?.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      }
    }
  }, [paginationOptions?.page, overlayScrollbarsInstance]);

  // Optimize data synchronization - only update if rows actually changed
  // Use ref to track previous row IDs and data reference to detect changes
  const prevRowIdsRef = useRef<string>('');
  const prevRowsRef = useRef(rows);
  useEffect(() => {
    // Compare row IDs to detect actual changes, not just reference changes
    const newRowIds = rows.map((r) => r.id).join(',');
    const rowIdsChanged = prevRowIdsRef.current !== newRowIds;
    const lengthChanged = data.length !== rows.length;
    // Also check if rows reference changed (important for when same leads are returned with updated data like tags)
    const rowsReferenceChanged = prevRowsRef.current !== rows;

    if (rowIdsChanged || lengthChanged || rowsReferenceChanged) {
      prevRowIdsRef.current = newRowIds;
      prevRowsRef.current = rows;
      setData(rows);
    }
  }, [rows, data.length]);

  // Track column order changes to ensure table updates when columns are reordered
  const prevColumnOrderRef = useRef<string>('');
  const columnOrderKey = useMemo(
    () =>
      `${columns.map((col) => col.id).join('-')}-pinned:${pinnedColumns.join(
        ',',
      )}`,
    [columns, pinnedColumns],
  );
  useEffect(() => {
    const currentColumnOrder = columns.map((col) => col.id).join('-');
    if (prevColumnOrderRef.current !== currentColumnOrder) {
      prevColumnOrderRef.current = currentColumnOrder;
      // Force table update by updating data reference
      // This ensures the table re-renders with the new column order
      setData((prevData) => [...prevData]);
    }
  }, [columns]);

  const selectedCount = selectedRowIds?.length || 0;
  const hasSelections = selectedCount > 0 || isSelectAllAcrossPages;

  const currentPageSelectedCount = useMemo(
    () => rowIds?.filter((id) => selectedRowIdsSet?.has(id))?.length,
    [rowIds, selectedRowIdsSet],
  );
  const showSelectAllButton = !isSelectAllAcrossPages && onSelectAllAcrossPages;
  const showAlert = hasSelections;

  const effectiveBulkSelectionLimit = useMemo(() => {
    const totalRows = paginationOptions?.totalRows || 0;
    const caps = [totalRows];
    if (maxSelectedRowsLimit != null && maxSelectedRowsLimit > 0) {
      caps.push(maxSelectedRowsLimit);
    }
    if (maxCustomLimit != null && maxCustomLimit > 0) {
      caps.push(maxCustomLimit);
    }
    return Math.min(...caps);
  }, [paginationOptions?.totalRows, maxSelectedRowsLimit, maxCustomLimit]);

  const cappedSelectAllTooltipText = useMemo(() => {
    if (!enableCappedSelectAllUI) return '';
    const totalRows = paginationOptions?.totalRows || 0;
    if (totalRows <= 0) return '';

    const maxCap =
      maxSelectedRowsLimit != null && maxSelectedRowsLimit > 0
        ? maxSelectedRowsLimit
        : null;
    const creditCap =
      maxCustomLimit != null && maxCustomLimit > 0 ? maxCustomLimit : null;

    const isCreditCapped =
      creditCap != null &&
      creditCap < totalRows &&
      // Only treat as "credit capped" when credits are the tightest constraint.
      // If credits are equal to or higher than the bulk cap, show "Maximum X..."
      (maxCap == null || creditCap < maxCap) &&
      effectiveBulkSelectionLimit === creditCap;

    if (isCreditCapped) {
      return `Selection limited to ${effectiveBulkSelectionLimit.toLocaleString()} records based on available credits`;
    }

    return `Maximum ${effectiveBulkSelectionLimit.toLocaleString()} can be selected`;
  }, [
    enableCappedSelectAllUI,
    paginationOptions?.totalRows,
    maxSelectedRowsLimit,
    maxCustomLimit,
    effectiveBulkSelectionLimit,
  ]);

  const shouldSuppressSelectAllRowTicks =
    isSelectAllAcrossPages &&
    isCustomLimitMode &&
    appliedLimit != null &&
    appliedLimit > 0 &&
    appliedLimit < effectiveBulkSelectionLimit;

  const handleSelectAllAcrossPages = useCallback(() => {
    if (!onSelectAllAcrossPages) return;

    onSelectAllAcrossPages();

    // Enforce caps for "select all": if the table has more rows than allowed,
    // immediately apply a limit so bulk actions never operate on more than allowed.
    // (Parent decides how to apply this limit, we just request it consistently.)
    const totalRows = paginationOptions?.totalRows || 0;
    if (onApplyLimit && effectiveBulkSelectionLimit > 0) {
      if (totalRows > effectiveBulkSelectionLimit) {
        onApplyLimit(effectiveBulkSelectionLimit);
      }
    }
  }, [
    onSelectAllAcrossPages,
    onApplyLimit,
    effectiveBulkSelectionLimit,
    paginationOptions?.totalRows,
  ]);

  const cappedSelectAllUiProps = useMemo(() => {
    if (!enableCappedSelectAllUI) return {};
    return {
      page: paginationOptions?.page || 1,
      pageSize: paginationOptions?.limit || 0,
      appliedLimit,
      enableCappedSelectAllUI,
    };
  }, [
    enableCappedSelectAllUI,
    paginationOptions?.page,
    paginationOptions?.limit,
    appliedLimit,
  ]);

  // Calculate display count for select all across pages
  const selectAllDisplayCount = useMemo(() => {
    if (!isSelectAllAcrossPages) return 0;
    // A user-applied limit takes precedence over every other count.
    if (appliedLimit !== null) {
      return appliedLimit;
    }
    // Use custom count if provided, otherwise calculate default
    if (customSelectAllDisplayCount !== null) {
      return customSelectAllDisplayCount;
    }
    const totalRows = paginationOptions?.totalRows || 0;
    if (maxSelectedRowsLimit != null && maxSelectedRowsLimit > 0) {
      return Math.min(totalRows, maxSelectedRowsLimit);
    }
    return totalRows;
  }, [
    isSelectAllAcrossPages,
    appliedLimit,
    customSelectAllDisplayCount,
    paginationOptions?.totalRows,
    maxSelectedRowsLimit,
  ]);

  // Column visibility handlers
  const handleColumnVisibilityClick = () => {
    setShowColumnModal(true);
  };

  const handleSaveColumns = (
    updatedFields: ColumnField[],
    newlyAddedColumns: ColumnField[],
  ) => {
    setSelectedFields(updatedFields);
    columnVisibility?.onSaveColumns(updatedFields, newlyAddedColumns);

    // Save preferences if callback is provided
    if (columnVisibility?.onSaveColumnPreferences) {
      columnVisibility.onSaveColumnPreferences(updatedFields);
    }

    setShowColumnModal(false);
  };

  const handleCancelColumnModal = () => {
    setShowColumnModal(false);
  };

  const handleResetColumns = () => {
    columnVisibility?.onResetColumns();
  };

  return (
    <div
      className={classNames(
        'tanstack-table',
        hasSelections && 'has-selection-alert',
        tableClassName,
      )}
    >
      {showBanner && Banner}
      {showAlert &&
        (showLeadSelectionBanner ? (
          <LeadSelectionBanner
            selectedCount={
              isSelectAllAcrossPages ? selectAllDisplayCount : selectedCount
            }
            pageCount={paginationOptions?.limit || 0}
            totalCount={paginationOptions?.totalRows || 0}
            appliedLimit={appliedLimit}
            maxCustomLimit={maxCustomLimit}
            bulkSelectionLimit={maxSelectedRowsLimit}
            isSelectAllAcrossPages={isSelectAllAcrossPages}
            currentPageSelectedCount={currentPageSelectedCount}
            onApplyCurrentPage={onApplyCurrentPage || NOOP}
            onApplyCustomLimit={onApplyLimit || NOOP}
            onSelectAll={
              showSelectAllButton ? handleSelectAllAcrossPages : undefined
            }
            onClearSelection={onClearSelection || undefined}
            itemName={itemName}
            freePlanSelectionLimit={freePlanSelectionLimit}
            freePlanLimitMessage={paginationRestrictionTooltip}
          />
        ) : (
          <Alert
            variant="alert"
            className="tanstack-table-clear-selection-alert w-100"
            description={
              <div className="d-flex align-items-center justify-content-between gap-8">
                <span>
                  {(() => {
                    const displayCount = isSelectAllAcrossPages
                      ? selectAllDisplayCount
                      : selectedCount;
                    if (enableProperPluralization) {
                      const displayName = pluralizeWord(itemName, displayCount);
                      return `${displayCount} ${displayName} Selected.`;
                    }
                    return isSelectAllAcrossPages
                      ? `${selectAllDisplayCount} ${itemName}(s) selected.`
                      : `${selectedCount} ${itemName}(s) selected.`;
                  })()}
                </span>
                <div className="d-flex align-items-center gap-8">
                  {showSelectAllButton && (
                    <Button
                      variant="link"
                      onClick={onSelectAllAcrossPages || undefined}
                      className="select-all-across-pages-button p-0"
                    >
                      {enableProperPluralization
                        ? `Select all ${pluralizeWord(itemName, 2)}`
                        : `Select all ${itemName}s`}
                    </Button>
                  )}
                  {hasSelections && <span className="alert-divider-vertical" />}
                  {hasSelections && onClearSelection && (
                    <Button
                      variant="link"
                      onClick={onClearSelection}
                      className="clear-selection-button p-0"
                    >
                      Clear Selection
                    </Button>
                  )}
                </div>
              </div>
            }
          />
        ))}
      <div className="tanstack-table--wrapper" ref={tableRef}>
        <div
          className="tanstack-table--table"
          style={{
            ...columnSizeVars,
            width: tableWidth,
          }}
        >
          <THead
            table={table}
            isLoading={isLoading}
            sort={sort}
            onSort={onSort}
            rows={rows}
            selectedRowIds={selectedRowIds}
            onRowSelectAllHandler={onRowSelectAllHandler}
            maxSelectedRowsLimit={maxSelectedRowsLimit}
            isSelectAllAcrossPages={isSelectAllAcrossPages}
            {...(cappedSelectAllUiProps as any)}
            allowIndivualRowDeSelectionAcrossAllPage={
              allowIndivualRowDeSelectionAcrossAllPage
            }
            onClearSelection={onClearSelection}
            isRowDisabled={isRowDisabled}
            enableColumnOrdering={enableColumnOrdering}
            enableRowSelection={enableRowSelection}
            pinnedColumns={pinnedColumns}
            onColumnOrderChange={onColumnOrderChange}
            defaultPinnedColumns={defaultPinnedColumns}
            immovableColumns={immovableColumns}
            onFreezeColumn={onFreezeColumn}
            onUnfreezeColumn={onUnfreezeColumn}
          />

          {isResizingColumn ? (
            <MemoizedTBody
              key={columnOrderKey}
              allowIndivualRowDeSelectionAcrossAllPage={
                allowIndivualRowDeSelectionAcrossAllPage
              }
              table={table}
              onRowClick={onRowClick}
              isLoading={isLoading}
              selectedRowIds={selectedRowIds}
              selectedRowIdsSet={selectedRowIdsSet}
              onRowSelectHandler={onRowSelectHandler}
              maxSelectedRowsLimit={maxSelectedRowsLimit}
              isSelectAllAcrossPages={isSelectAllAcrossPages}
              {...(cappedSelectAllUiProps as any)}
              isCustomLimitMode={shouldSuppressSelectAllRowTicks}
              cellRenderer={cellRenderer}
              isRowDisabled={isRowDisabled}
              getRowDisabledTooltip={getRowDisabledTooltip}
              enableCappedSelectAllUI={enableCappedSelectAllUI}
              cappedSelectAllTooltipText={cappedSelectAllTooltipText}
              enableRowSelection={enableRowSelection}
              enableExpandedRows={enableExpandedRows}
              getRowClassName={getRowClassName}
            />
          ) : (
            <TBody
              allowIndivualRowDeSelectionAcrossAllPage={
                allowIndivualRowDeSelectionAcrossAllPage
              }
              key={columnOrderKey}
              table={table}
              onRowClick={onRowClick}
              isLoading={isLoading}
              selectedRowIds={selectedRowIds}
              selectedRowIdsSet={selectedRowIdsSet}
              onRowSelectHandler={onRowSelectHandler}
              maxSelectedRowsLimit={maxSelectedRowsLimit}
              isSelectAllAcrossPages={isSelectAllAcrossPages}
              {...(cappedSelectAllUiProps as any)}
              isCustomLimitMode={shouldSuppressSelectAllRowTicks}
              cellRenderer={cellRenderer}
              isRowDisabled={isRowDisabled}
              getRowDisabledTooltip={getRowDisabledTooltip}
              enableCappedSelectAllUI={enableCappedSelectAllUI}
              cappedSelectAllTooltipText={cappedSelectAllTooltipText}
              enableRowSelection={enableRowSelection}
              enableExpandedRows={enableExpandedRows}
              getRowClassName={getRowClassName}
            />
          )}
        </div>
      </div>

      {!isLoading && paginationOptions?.totalRows ? (
        <TablePagination
          page={paginationOptions.page}
          limit={paginationOptions.limit}
          totalRows={paginationOptions.totalRows}
          onPaginationOptionsChange={
            paginationOptions.onPaginationOptionsChange
          }
          customOptions={paginationSizeOptions}
          disableGoToPage={disableGoToPage}
          maxPaginationCount={maxPaginationCount}
          maxAllowedPage={maxAllowedPage}
          paginationRestrictionTooltip={paginationRestrictionTooltip}
        />
      ) : (
        <span />
      )}

      {/* Column Visibility Control */}
      {columnVisibility?.enableColumnVisibility && !isLoading && (
        <ColumnVisibilityControl
          onClick={handleColumnVisibilityClick}
          isLoading={isLoading}
        />
      )}

      {/* Edit Columns Modal */}
      {columnVisibility?.enableColumnVisibility && showColumnModal && (
        <EditTableColumnsModal
          show={showColumnModal}
          onCancel={handleCancelColumnModal}
          onSave={handleSaveColumns}
          selectedFields={selectedFields}
          columnGroups={columnVisibility.columnGroups}
          priorityArray={columnVisibility.priorityArray}
          onResetColumns={handleResetColumns}
          showSelectAll={true}
        />
      )}
    </div>
  );
};

export default TanStackTable;
